import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Copy,
  LogOut,
  Mail,
  RefreshCw,
  Search,
  ChevronDown,
  DollarSign,
  Users,
  TrendingUp,
  UserPlus,
  Inbox,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";

const formatDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const usd = (v) =>
  v == null
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(v);

const STATUS_STYLES = {
  active: "bg-[#0F4C5C] text-white",
  trialing: "bg-[#166479] text-white",
  past_due: "bg-[#D67229] text-white",
  canceled: "bg-[#D3D9DE] text-[#2C333A]",
  incomplete: "bg-[#8593A1] text-white",
  incomplete_expired: "bg-[#8593A1] text-white",
  unpaid: "bg-[#7A3906] text-white",
  paused: "bg-[#D3D9DE] text-[#2C333A]",
};

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || "bg-[#D3D9DE] text-[#2C333A]";
  return (
    <span
      data-testid={`sub-status-${status}`}
      className={`inline-flex items-center font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${cls}`}
    >
      {status?.replace(/_/g, " ")}
    </span>
  );
}

function StatCard({ label, value, sub, icon: Icon, accent = "#0F4C5C", testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-white border border-[#D3D9DE] p-6 flex flex-col justify-between h-full"
    >
      <div className="flex items-start justify-between">
        <p className="gp-overline" style={{ color: accent }}>
          {label}
        </p>
        {Icon ? (
          <span
            aria-hidden
            className="h-9 w-9 border border-[#111418] flex items-center justify-center"
            style={{ color: accent }}
          >
            <Icon size={16} strokeWidth={1.6} />
          </span>
        ) : null}
      </div>
      <div className="mt-4">
        <p
          className="font-display text-3xl md:text-4xl leading-none"
          style={{ color: "#111418" }}
        >
          {value}
        </p>
        {sub ? (
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#2C333A]">
            {sub}
          </p>
        ) : null}
      </div>
    </div>
  );
}

export default function AdminStripe() {
  const { user, authApi, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const fetchDashboard = useCallback(
    async (force = false) => {
      setError("");
      if (force) setRefreshing(true);
      else setLoading(true);
      try {
        const res = await authApi.get("/admin/stripe/dashboard", {
          params: force ? { force_refresh: true } : {},
        });
        setData(res.data);
      } catch (err) {
        const code = err?.response?.status;
        if (code === 401 || code === 403) {
          toast.error("Admin access required.");
          navigate("/dashboard", { replace: true });
          return;
        }
        const detail = err?.response?.data?.detail || "Failed to load Stripe data.";
        setError(detail);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authApi, navigate]
  );

  useEffect(() => {
    fetchDashboard(false);
  }, [fetchDashboard]);

  const metrics = data?.metrics || {};
  const subscribers = data?.subscribers || [];

  const mrrUsd = metrics.mrr?.usd?.amount;
  const revenueUsd = metrics.total_revenue_collected?.usd?.amount;

  const filteredSubs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return subscribers.filter((s) => {
      if (filter !== "all" && s.status !== filter) return false;
      if (!q) return true;
      return [s.name, s.email, s.id].some((v) =>
        (v || "").toLowerCase().includes(q)
      );
    });
  }, [subscribers, filter, search]);

  const statusCounts = useMemo(() => {
    const counts = { active: 0, past_due: 0, canceled: 0, other: 0 };
    subscribers.forEach((s) => {
      if (counts[s.status] !== undefined) counts[s.status] += 1;
      else counts.other += 1;
    });
    return counts;
  }, [subscribers]);

  return (
    <div
      className="min-h-screen bg-[#F4F3EE] text-[#111418]"
      data-testid="admin-stripe-page"
    >
      <header className="border-b border-[#D3D9DE] bg-[#F4F3EE]/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="gp-container h-16 md:h-20 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              data-testid="admin-back-button"
              className="inline-flex items-center gap-2 group"
            >
              <ArrowLeft
                size={14}
                className="text-[#2C333A] group-hover:text-[#D67229] transition-colors"
              />
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2C333A] group-hover:text-[#D67229] transition-colors">
                Dashboard
              </span>
            </button>
            <div className="hidden sm:flex items-center gap-2">
              <span aria-hidden className="inline-block h-2 w-2 bg-[#D67229]" />
              <span className="font-display text-xl font-bold tracking-tight">
                GushPay <span className="text-[#0F4C5C] italic">/ admin</span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/admin/leads")}
              data-testid="admin-nav-leads"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#2C333A] hover:text-[#D67229] transition-colors hidden md:inline-flex items-center gap-2"
            >
              <Inbox size={12} /> Legacy leads
            </button>
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#2C333A] hidden lg:inline">
              {user?.email}
            </span>
            <button
              type="button"
              onClick={async () => {
                await logout();
                navigate("/", { replace: true });
              }}
              data-testid="admin-logout"
              className="gp-btn gp-btn-ghost"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="gp-container py-12 md:py-16">
        {/* Title */}
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <span className="gp-overline">// Live Stripe pipeline</span>
            <h1 className="mt-4 font-display text-4xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
              Subscribers &{" "}
              <span className="italic text-[#0F4C5C]">revenue.</span>
            </h1>
            <p className="mt-3 text-[#2C333A] max-w-xl">
              Live snapshot from Stripe — MRR, active subscribers, and every
              customer paying for GushPay Access. Data refreshes automatically
              every 5 minutes.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {data?.as_of ? (
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#8593A1]">
                Snapshot · {new Date(data.as_of).toLocaleTimeString()}
              </span>
            ) : null}
            <button
              type="button"
              onClick={() => fetchDashboard(true)}
              disabled={refreshing || loading}
              data-testid="admin-refresh"
              className="gp-btn gp-btn-ghost disabled:opacity-50"
            >
              <RefreshCw
                size={14}
                className={refreshing ? "animate-spin" : ""}
              />
              Refresh
            </button>
          </div>
        </div>

        {/* Not configured banner */}
        {data && !data.configured && (
          <div
            data-testid="admin-not-configured"
            className="mb-10 border border-[#D67229]/60 bg-[#D67229]/10 px-6 py-5 flex items-start gap-4"
          >
            <span
              aria-hidden
              className="mt-1 inline-flex h-8 w-8 items-center justify-center bg-[#D67229] text-white flex-shrink-0"
            >
              <AlertTriangle size={16} strokeWidth={2} />
            </span>
            <div>
              <p className="font-display text-lg text-[#111418]">
                Stripe isn&apos;t connected yet.
              </p>
              <p className="mt-1 text-sm text-[#2C333A] leading-relaxed max-w-2xl">
                {data.message}
              </p>
              <ol className="mt-4 space-y-1 text-sm text-[#2C333A] list-decimal list-inside">
                <li>
                  Get a Restricted API key at{" "}
                  <a
                    href="https://dashboard.stripe.com/apikeys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#D67229] underline underline-offset-4"
                  >
                    dashboard.stripe.com/apikeys
                  </a>{" "}
                  with <span className="font-mono">Read</span> permissions for
                  Subscriptions, Customers, and Balance transactions.
                </li>
                <li>
                  Add it to your production environment as{" "}
                  <span className="font-mono">STRIPE_SECRET_KEY</span> and
                  redeploy.
                </li>
              </ol>
            </div>
          </div>
        )}

        {/* Error banner */}
        {error && (
          <div
            data-testid="admin-error"
            className="mb-10 border border-[#7A3906]/60 bg-[#7A3906]/10 px-6 py-5 text-[#7A3906] text-sm"
          >
            {error}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
          <StatCard
            label="Active subscribers"
            testId="stat-active"
            value={metrics.active_subscribers ?? 0}
            sub={`${metrics.total_subscribers ?? 0} total in Stripe`}
            icon={Users}
            accent="#0F4C5C"
          />
          <StatCard
            label="Monthly recurring"
            testId="stat-mrr"
            value={usd(mrrUsd || 0)}
            sub="MRR · Active subscriptions"
            icon={TrendingUp}
            accent="#D67229"
          />
          <StatCard
            label="Total collected"
            testId="stat-revenue"
            value={usd(revenueUsd || 0)}
            sub="Gross charges − refunds"
            icon={DollarSign}
            accent="#0F4C5C"
          />
          <StatCard
            label="New this month"
            testId="stat-new"
            value={metrics.new_subscriptions_this_month ?? 0}
            sub={`${metrics.cancellations_this_month ?? 0} canceled`}
            icon={UserPlus}
            accent="#166479"
          />
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            {[
              { id: "all", label: `All (${subscribers.length})` },
              { id: "active", label: `Active (${statusCounts.active})` },
              { id: "past_due", label: `Past due (${statusCounts.past_due})` },
              { id: "canceled", label: `Canceled (${statusCounts.canceled})` },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                data-testid={`admin-filter-${f.id}`}
                className={`h-9 px-4 font-mono text-[11px] uppercase tracking-[0.2em] border transition-colors ${
                  filter === f.id
                    ? "bg-[#0F4C5C] text-white border-[#0F4C5C]"
                    : "bg-transparent text-[#2C333A] border-[#D3D9DE] hover:border-[#111418]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="md:ml-auto flex items-center gap-2 bg-white border border-[#D3D9DE] px-3 h-10 md:min-w-[280px]">
            <Search size={14} className="text-[#8593A1]" />
            <input
              data-testid="admin-search"
              type="search"
              placeholder="Search name, email, sub ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="bg-transparent outline-none text-sm flex-1 placeholder:text-[#8593A1]"
            />
          </div>
        </div>

        {/* Subscribers list */}
        <div className="bg-white border border-[#D3D9DE]" data-testid="admin-subs-list">
          {loading ? (
            <div className="px-6 py-16 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-[#8593A1]">
              Loading Stripe data…
            </div>
          ) : filteredSubs.length === 0 ? (
            <div className="px-6 py-16 text-center" data-testid="admin-subs-empty">
              <p className="gp-overline">
                {data?.configured ? "// No subscribers yet" : "// Not connected"}
              </p>
              <p className="mt-4 font-display text-xl text-[#111418]">
                {data?.configured
                  ? "You'll see your first paying operator here."
                  : "Connect Stripe to start seeing data."}
              </p>
              <p className="mt-2 text-sm text-[#2C333A] max-w-md mx-auto">
                {data?.configured
                  ? "Every time someone signs up through the Stripe Payment Link, they'll appear on this list within a few minutes."
                  : "See the banner above for setup instructions."}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-[#E9ECEF]">
              {filteredSubs.map((s) => (
                <li
                  key={s.id}
                  data-testid={`admin-sub-row-${s.id}`}
                  className="px-5 md:px-8 py-5"
                >
                  <div className="flex flex-col md:flex-row md:items-start md:gap-6">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-3">
                        <p className="font-display text-lg md:text-xl text-[#111418] truncate">
                          {s.name || (
                            <span className="text-[#8593A1] italic font-sans text-base">
                              No name on file
                            </span>
                          )}
                        </p>
                        <StatusPill status={s.status} />
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-[#2C333A]">
                        {s.email ? (
                          <span className="inline-flex items-center gap-2">
                            <Mail size={12} className="text-[#0F4C5C]" />
                            <a
                              href={`mailto:${s.email}`}
                              className="hover:text-[#D67229] transition-colors"
                            >
                              {s.email}
                            </a>
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(s.email);
                                  toast.success("Email copied");
                                } catch (_e) {
                                  toast.error("Could not copy");
                                }
                              }}
                              aria-label="Copy email"
                              data-testid={`admin-sub-copy-${s.id}`}
                              className="text-[#8593A1] hover:text-[#0F4C5C] transition-colors"
                            >
                              <Copy size={12} />
                            </button>
                          </span>
                        ) : null}
                        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#8593A1]">
                          Started {formatDate(s.subscription_start_date)}
                        </span>
                        {s.current_period_end ? (
                          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#0F4C5C]">
                            Renews {formatDate(s.current_period_end)}
                            {s.cancel_at_period_end ? " · canceling" : ""}
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8593A1]">
                        {s.id}
                      </p>
                    </div>
                    <a
                      href={`https://dashboard.stripe.com/subscriptions/${s.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`admin-sub-open-${s.id}`}
                      className="mt-3 md:mt-0 gp-btn gp-btn-ghost self-start md:self-auto"
                    >
                      Open in Stripe
                      <ChevronDown size={12} className="-rotate-45" />
                    </a>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.22em] text-[#8593A1]">
          Showing {filteredSubs.length} of {subscribers.length} · Data cached 5
          min · Live source: Stripe
        </p>
      </main>
    </div>
  );
}
